import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useGeneralBottomSheetModal } from '@euler/functions/useBottomSheetModal';
import { useBehaviorSubject, useContextBridge } from '@euler/utils/hooks';
import { BottomSheetFooter, BottomSheetModal } from '@gorhom/bottom-sheet';
import { NavigationContainerRef } from '@react-navigation/native';
import {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  SiteInspectionNavParams,
  SiteInspectionNavigator,
} from './SiteInspectionNavigator';

import {
  SiteInspectionNavigatorContext,
  useSiteInspectionNavigatorContext,
} from './SiteInspectionContext';
import { SiteInspectionFlowRef } from './types';

export const SiteInspectionBottomSheetFlow = memo(
  forwardRef<
    SiteInspectionFlowRef,
    { onDismiss?: (result?: any) => void; onPresent?: () => void }
  >(({ onDismiss, onPresent }, ref) => {
    const { rpx } = useResponsiveContext();
    const [initialScreen, updateInitialScreen] = useState<{
      screen: keyof SiteInspectionNavParams;
      params?: any;
    }>();

    const finishWithResult = useRef<any>();

    const navigatorRef =
      useRef<NavigationContainerRef<SiteInspectionNavParams>>(null);

    const { bottomComponent$, preventNavigateAway$, onWillDismiss$ } =
      useSiteInspectionNavigatorContext();
    const [isNavigateAwayPrevented] = useBehaviorSubject(preventNavigateAway$);
    const [bottomComponent] = useBehaviorSubject(bottomComponent$);

    const { bottomSheetModalRef, ...bottomSheetProps } =
      useGeneralBottomSheetModal({
        show: initialScreen != null,
        enableDismissOnClose: true,
        enablePanDownToClose: !isNavigateAwayPrevented,
        dismissWhenPressBackdrop: !isNavigateAwayPrevented,
        minSnapPoint: '50%',
        mediumSnapPoint: '75%',
      });

    const isDismissed = useRef(false);

    const onInternalDismiss = useCallback(() => {
      if (isDismissed.current) return;

      let isDefaultPrevented = false;

      const dismiss = () => {
        isDismissed.current = true;

        bottomSheetModalRef.current?.dismiss();
        updateInitialScreen(undefined);
        onDismiss?.(finishWithResult.current);
        finishWithResult.current = undefined;
        preventNavigateAway$.next(false);
      };

      const event = {
        preventDefault: () => {
          isDefaultPrevented = true;
        },
        dismiss,
      };

      onWillDismiss$.next(event);

      if (isDefaultPrevented) {
        return;
      }

      dismiss();
    }, [bottomSheetModalRef, onDismiss, onWillDismiss$, preventNavigateAway$]);

    useImperativeHandle(ref, () => {
      return {
        get navigator() {
          return navigatorRef.current!;
        },
        present<S extends keyof SiteInspectionNavParams>(
          screen: S,
          params: SiteInspectionNavParams[S],
        ) {
          updateInitialScreen({ screen, params });
          onPresent?.();
        },
        finish(result?: any) {
          finishWithResult.current = result;
          if (bottomSheetModalRef.current == null) {
            onInternalDismiss();
          } else {
            bottomSheetModalRef.current.dismiss();
          }
        },
      };
    });

    const ContextBridge = useContextBridge(SiteInspectionNavigatorContext);

    const Footer = useCallback(
      (props: any) => {
        return bottomComponent ? (
          <BottomSheetFooter {...props}>{bottomComponent}</BottomSheetFooter>
        ) : null;
      },
      [bottomComponent],
    );

    if (initialScreen == null) {
      return null;
    }

    isDismissed.current = false;

    return (
      <BottomSheetModal
        ref={bottomSheetModalRef}
        {...bottomSheetProps}
        onDismiss={onInternalDismiss}
        footerComponent={Footer}
        handleIndicatorStyle={{
          opacity: 0,
        }}
        handleStyle={{
          padding: 0,
          height: rpx(12),
        }}
      >
        <ContextBridge>
          <SiteInspectionNavigator
            ref={navigatorRef}
            initialScreen={initialScreen.screen}
            initialParams={initialScreen.params}
            onDismiss={onInternalDismiss}
          />
        </ContextBridge>
      </BottomSheetModal>
    );
  }),
);
